import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { EmptyState } from "@/components/dashboard/shared/EmptyState";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { PaymentHistoryResponse } from "@/types/invoice";
import { RiSearchLine, RiMoneyDollarCircleLine, RiArrowLeftLine } from "@remixicon/react";

const METHODS = [
  { label: "All Methods", value: "all" },
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
  { label: "Card", value: "card" },
  { label: "Mobile Money", value: "mobile_money" },
  { label: "Cheque", value: "cheque" },
  { label: "Other", value: "other" },
];

export function InvoicePaymentsPage() {
  const { storeId, currentStore } = useCurrentStore();
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("all");
  const [page, setPage] = useState(1);

  const currency = currentStore?.currency || "NGN";
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(n);

  const { data, isLoading } = useQuery<PaymentHistoryResponse>({
    queryKey: ["invoice-payments", storeId, search, method, page],
    queryFn: async () => {
      const { data } = await api.get("/api/invoices/payments", {
        params: { search, method, page, limit: 20 },
      });
      return data;
    },
    enabled: !!storeId,
  });

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Payment History" />
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard/invoices">
            <RiArrowLeftLine className="h-4 w-4 mr-1.5" />
            Invoices
          </Link>
        </Button>
        <PageHeader title="Payment History" subtitle="All payments recorded across invoices" />
      </div>

      {/* Total received banner */}
      {data && (
        <Card className="p-4 flex items-center gap-4 bg-green-50 border-green-200">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
            <RiMoneyDollarCircleLine className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs text-green-700 font-medium uppercase tracking-wide">Total received (filtered)</div>
            <div className="text-2xl font-bold text-green-800">{fmt(data.totalReceived)}</div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by invoice # or customer name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className={`${dashboardStyles.filterInput} pl-9`}
          />
        </div>
        <Select value={method} onValueChange={(v) => { setMethod(v); setPage(1); }}>
          <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-48`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {METHODS.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Payment records table */}
      <Card className="overflow-hidden rounded-[1.25rem] border border-[#EBEBEB] shadow-none">
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-4 px-5 py-4">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20 ml-auto" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : !data?.data.length ? (
          <EmptyState
            icon={<RiMoneyDollarCircleLine className="h-8 w-8 text-muted-foreground" />}
            title="No payment records"
            description="Payments recorded on invoices will appear here."
          />
        ) : (
          <>
            {/* Header */}
            <div className="hidden sm:grid grid-cols-[1fr_1.5fr_1fr_1fr_1fr] gap-4 px-5 py-3 border-b border-[#EBEBEB] text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <span>Invoice</span>
              <span>Customer</span>
              <span>Amount</span>
              <span>Method</span>
              <span className="text-right">Date</span>
            </div>
            <div className="divide-y">
              {data.data.map((p) => (
                <div
                  key={p._id}
                  className="flex flex-col sm:grid sm:grid-cols-[1fr_1.5fr_1fr_1fr_1fr] gap-2 sm:gap-4 items-start sm:items-center px-5 py-4"
                >
                  <Link
                    to={`/dashboard/invoices/${p.invoiceId}`}
                    className="font-mono text-sm font-medium text-primary hover:underline"
                  >
                    {p.invoiceNumber}
                  </Link>
                  <span className="text-sm">{p.clientName}</span>
                  <span className="text-sm font-medium text-green-700">{fmt(p.amountPaid)}</span>
                  <span className="w-fit rounded-full bg-[#F5F5F3] px-2.5 py-0.5 text-xs font-medium capitalize">
                    {p.paymentMethod.replace("_", " ")}
                  </span>
                  <span className="text-xs text-muted-foreground sm:text-right">
                    {new Date(p.datePaid).toLocaleDateString("en-NG", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-[#EBEBEB] px-5 py-3 text-sm text-muted-foreground">
                <span>Page {data.pagination.currentPage} of {data.pagination.totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className={dashboardStyles.paginationBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" className={dashboardStyles.paginationBtn} disabled={page >= data.pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
